// TAX & HONOR panel — set how hard you tax your citizens (the income↔Honor
// tradeoff) and see your passive tribute + Honor rating. Toggled from the TAX
// rail button.

import { Events, on } from './eventBus.js'
import { GoonTypes, ClickEconomy, GoonConfig, PlotTypes, RiceConfig, TaxConfig } from '../config/balance.js'
import { getGoonsOwned, getInjuredCount, getTaxRate, setTaxRate } from '../game/seasonManager.js'
import { countByType } from '../game/plotState.js'
import { getRate } from '../game/taxManager.js'
import { getHonorDisplay } from '../game/honor.js'
import { makeModalToggleable } from './railPanel.js'
import { pushLog } from './hud.js'
import { formatCoins } from './format.js'

// CR-3/4: citizens earn a flat idle rate (peasant 1/s, scribe 2/s); only these
// two types produce tribute (noble cats are out of scope).
const IDLE = {
  [GoonTypes.PEASANT_CAT]: ClickEconomy.peasantIdlePerSec,
  [GoonTypes.SCRIBE_CAT]: ClickEconomy.scribeIdlePerSec,
}
const CITIZEN_TYPES = [GoonTypes.PEASANT_CAT, GoonTypes.SCRIBE_CAT]

export function mountTaxPanel({ root, toggleBtn }) {
  root.innerHTML = `
    <div class="hud__build-header">
      <span class="hud__build-title">TAX & HONOR</span>
      <span class="hud__build-tiles" data-rate>—</span>
    </div>
    <div class="hud__tax-rate">
      <div class="hud__tax-rate-head">
        <span>Tax rate <strong data-tax-pct>—</strong></span>
        <span>Honor <strong class="hud__honor" data-honor>—</strong> / 10</span>
      </div>
      <input type="range" min="0" max="${Math.round(TaxConfig.maxRate * 100)}" step="5" value="${Math.round(getTaxRate() * 100)}" data-tax-slider class="hud__tax-slider">
      <div class="hud__tax-rate-hint" data-tax-hint></div>
    </div>
    <div class="hud__build-list" data-rows>
      ${CITIZEN_TYPES.map(rowTemplate).join('')}
      <div class="hud__build-row" data-tax-rice>
        <div class="hud__build-info">
          <div class="hud__build-name">Rice Paddies</div>
          <div class="hud__build-desc">${RiceConfig.passivePerTile}m / sec each — grows while you're away</div>
        </div>
        <div class="hud__build-owned" data-rice-owned>0</div>
        <div class="hud__build-owned" data-rice-contrib>0m / SEC</div>
      </div>
      <div class="hud__build-row" data-tax-injured>
        <div class="hud__build-info">
          <div class="hud__build-name">Injured Citizens</div>
          <div class="hud__build-desc">Earn 0 until healed — Feed, or send to a Hospital/Temple</div>
        </div>
        <div class="hud__build-owned" data-injured-owned>0</div>
        <div class="hud__build-owned" data-injured-contrib>0m / SEC</div>
      </div>
    </div>
    <div class="hud__tax-note">Tribute accrues even while you're away (capped at 8h). Higher tax = more income now, but lower Honor.</div>
  `

  const rateEl = root.querySelector('[data-rate]')
  const pctEl = root.querySelector('[data-tax-pct]')
  const honorEl = root.querySelector('[data-honor]')
  const hintEl = root.querySelector('[data-tax-hint]')
  const slider = root.querySelector('[data-tax-slider]')
  const riceOwnedEl = root.querySelector('[data-rice-owned]')
  const riceContribEl = root.querySelector('[data-rice-contrib]')
  const injOwnedEl = root.querySelector('[data-injured-owned]')
  const injContribEl = root.querySelector('[data-injured-contrib]')
  const rowEls = new Map()
  for (const id of CITIZEN_TYPES) {
    const row = root.querySelector(`[data-tax-row="${id}"]`)
    rowEls.set(id, { ownedEl: row.querySelector('[data-owned]'), contribEl: row.querySelector('[data-contrib]') })
  }

  slider.addEventListener('input', (e) => {
    setTaxRate(Number(e.target.value) / 100) // emits TaxRateChange → refresh
  })

  const refresh = () => {
    const taxMult = 1 + getTaxRate()
    rateEl.textContent = `${formatCoins(getRate())} / SEC`
    rateEl.classList.toggle('active', getRate() > 0)
    pctEl.textContent = `${Math.round(getTaxRate() * 100)}%`
    honorEl.textContent = getHonorDisplay()
    if (Number(slider.value) !== Math.round(getTaxRate() * 100)) slider.value = Math.round(getTaxRate() * 100)
    hintEl.textContent = getTaxRate() <= TaxConfig.minRate
      ? 'Benevolent — your citizens love you (max Honor from tax).'
      : getTaxRate() >= TaxConfig.maxRate
        ? 'Tyrannical — coin floods in, but your Honor suffers.'
        : 'Balanced — a fair levy on your citizens.'
    for (const id of CITIZEN_TYPES) {
      const owned = getGoonsOwned(id)
      rowEls.get(id).ownedEl.textContent = String(owned)
      rowEls.get(id).contribEl.textContent = `${formatCoins(owned * IDLE[id] * taxMult)} / SEC`
    }
    const farms = countByType(PlotTypes.FARM)
    riceOwnedEl.textContent = String(farms)
    riceContribEl.textContent = `${formatCoins(farms * RiceConfig.passivePerTile)} / SEC`
    injOwnedEl.textContent = String(getInjuredCount())
    injContribEl.textContent = '0m / SEC'
  }

  on(Events.TaxRateChange, refresh)
  on(Events.StreakChange, refresh)   // engagement changes Honor
  on(Events.ProfileChange, refresh)  // degrees change Honor
  on(Events.CitizenPromoted, refresh) // civic promotions change Honor
  on(Events.GoonOwnedChange, refresh)
  on(Events.CitizenInjured, refresh)
  on(Events.CitizenHealed, refresh)
  on(Events.WorkModeChange, refresh)
  on(Events.PlotChanged, refresh)
  on(Events.KingdomChange, refresh)
  on(Events.TaxOfflineEarned, ({ amount, seconds }) => {
    pushLog(`Tribute collected: ${formatCoins(amount)} from ${formatDuration(seconds)} away.`)
  })

  refresh()
  makeModalToggleable(root, toggleBtn)
}

function formatDuration(sec) {
  if (sec < 60) return `${sec}s`
  if (sec < 3600) return `${Math.floor(sec / 60)}m`
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`
}

function rowTemplate(id) {
  const cfg = GoonConfig[id]
  return `
    <div class="hud__build-row" data-tax-row="${id}">
      <div class="hud__build-info">
        <div class="hud__build-name">${cfg.name}</div>
        <div class="hud__build-desc">${IDLE[id]}m / sec each (idle) × tax</div>
      </div>
      <div class="hud__build-owned" data-owned>0</div>
      <div class="hud__build-owned" data-contrib>0m / SEC</div>
    </div>
  `
}
